import React, { useEffect, useState } from 'react';
import useResponsive from '../utils/useResponsive';
import { formatearMoneda } from '../utils/formatMoney';
import FiltroUsuarios from './FiltroUsuarios';
import { useFiltroUsuarios } from '../filtroUsuarios';

const API = '/api';

const money = (v) => `$${formatearMoneda(Math.round(Number(v) || 0))}`;

const SeguimientoDashboard = ({ onVerListado, onVerDetalleActivas, onVerReportes, onVerNotificaciones, onVerHistorico }) => {
  const { isMobile } = useResponsive();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [notifCount, setNotifCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const filtroUsuariosKey = useFiltroUsuarios().join(',');

  // Se recarga también al cambiar el filtro de usuarios del Administrador Plus.
  useEffect(() => {
    fetch(`${API}/seguimiento-clientes/dashboard`)
      .then(r => r.json())
      .then(d => (d.error ? setError(d.error) : setDatos(d)))
      .catch(() => setError('No se pudo cargar el Dashboard'));

    fetch(`${API}/seguimiento-clientes/notificaciones`)
      .then(r => r.json())
      .then(d => { if (!d.error) setNotifCount((d.proximos_firmar?.length || 0) + (d.vigencia?.length || 0)); })
      .catch(() => {});
  }, [filtroUsuariosKey]);

  useEffect(() => {
    // Logo de la hipotecaria/marca del broker (mismo que sale en el PDF del
    // Comparativo). El endpoint está protegido, así que se carga como blob.
    let url = null;
    fetch(`${API}/mi-hipotecaria-marca/logo`)
      .then(r => (r.ok ? r.blob() : null))
      .then(b => { if (b) { url = URL.createObjectURL(b); setLogoUrl(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, []);

  const st = {
    container: { padding: isMobile ? 12 : 24, maxWidth: 1500, margin: '0 auto' },
    // ~5 cm (≈190px) entre el título y el logo, pedido por el usuario.
    header: { marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: isMobile ? 24 : 190 },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 2fr) minmax(0, 1fr)',
      gap: 24,
    },
    card: {
      backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: isMobile ? 18 : 26,
    },
    // Variante ~75% de alta que st.card, solo para las tarjetas "Hipotecas
    // Firmadas" y "Por Cobrar" (se veían muy altas y obligaban a hacer
    // scroll para llegar a Acciones Rápidas).
    cardCompact: {
      backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: isMobile ? 14 : 19,
    },
    cardTitle: (accent) => ({
      fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', margin: '0 0 18px',
      paddingBottom: 10, borderBottom: `2px solid ${accent}`,
    }),
    cardTitleCompact: (accent) => ({
      fontSize: 16, fontWeight: 'bold', color: '#1a3a6e', margin: '0 0 13px',
      paddingBottom: 7, borderBottom: `2px solid ${accent}`,
    }),
    subblocks: { display: 'grid', gridTemplateColumns: `repeat(4, minmax(0, 1fr))`, gap: 12 },
    subblock: (bg) => ({
      backgroundColor: bg, borderRadius: 10, padding: '16px 4px', textAlign: 'center',
      minWidth: 0, overflow: 'hidden', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
      width: '100%', transition: 'filter 0.15s',
    }),
    subblockLbl: (color) => ({ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.2, marginBottom: 10, color, opacity: 0.85 }),
    subblockQty: (color) => ({ fontSize: 26, fontWeight: 'bold', color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }),
    subblockAmt: { fontSize: 13, fontWeight: 600, marginTop: 6, color: '#6b7280', fontVariantNumeric: 'tabular-nums', overflowWrap: 'break-word' },
    twoCol: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', height: '100%' },
    twoColItem: (bordered) => ({ textAlign: 'center', padding: '6px 8px', display: 'flex', flexDirection: 'column', justifyContent: 'center', borderLeft: bordered ? '1px solid #e5e7eb' : 'none' }),
    twoColLbl: { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: '#6b7280', marginBottom: 10 },
    twoColQty: (color) => ({ fontSize: 30, fontWeight: 'bold', color, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }),
    twoColAmt: (color) => ({ fontSize: 19, fontWeight: 'bold', color, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }),
    // Compactas (~75%): solo para la tarjeta "Por Cobrar".
    twoColItemCompact: (bordered) => ({ textAlign: 'center', padding: '4px 6px', display: 'flex', flexDirection: 'column', justifyContent: 'center', borderLeft: bordered ? '1px solid #e5e7eb' : 'none' }),
    twoColLblCompact: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: '#6b7280', marginBottom: 7 },
    twoColQtyCompact: (color) => ({ fontSize: 23, fontWeight: 'bold', color, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }),
    twoColAmtCompact: (color) => ({ fontSize: 15, fontWeight: 'bold', color, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }),
    statCards: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 11 },
    statCard: (bg) => ({
      backgroundColor: bg, borderRadius: 10, padding: '15px 12px', textAlign: 'center',
      border: 'none', cursor: 'pointer', fontFamily: 'inherit', width: '100%', transition: 'filter 0.15s',
    }),
    statCardIcon: { fontSize: 17, marginBottom: 4 },
    statCardLbl: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: '#6b7280', marginBottom: 6 },
    statCardQty: { fontSize: 26, fontWeight: 'bold', color: '#1a3a6e', fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
    statCardAmt: (color) => ({ fontSize: 13, fontWeight: 'bold', color, marginTop: 6, fontVariantNumeric: 'tabular-nums' }),
    accionesRow: { display: 'flex', gap: 14, flexWrap: 'wrap' },
    btnPrimary: { border: 'none', borderRadius: 8, padding: '15px 28px', fontSize: 15.5, fontWeight: 'bold', cursor: 'pointer', backgroundColor: '#1a3a6e', color: 'white' },
    btnNotif: { border: 'none', borderRadius: 8, padding: '15px 28px', fontSize: 15.5, fontWeight: 'bold', cursor: 'pointer', backgroundColor: '#1a3a6e', color: 'white', display: 'flex', alignItems: 'center', gap: 8 },
    badgeCount: { fontSize: 11.5, fontWeight: 'bold', backgroundColor: '#dc2626', color: 'white', padding: '2px 8px', borderRadius: 20, minWidth: 18, textAlign: 'center' },
  };

  if (error) return <div style={st.container}><p style={{ color: '#dc2626' }}>{error}</p></div>;
  if (!datos) return <div style={st.container}>Cargando...</div>;

  const { activas, en_proceso_firma, firmadas, por_cobrar } = datos;
  const delMes = firmadas.meses[new Date().getMonth()];

  return (
    <div style={st.container}>
      <div style={st.header}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 24, fontWeight: 'bold', margin: 0, color: '#1a3a6e' }}>🤝 CRM Hipotecas</h2>
          <FiltroUsuarios ancho={280} />
        </div>
        {logoUrl && (
          <img src={logoUrl} alt="Logo de la empresa" style={{ maxHeight: isMobile ? 48 : 72, maxWidth: isMobile ? 180 : 300, objectFit: 'contain' }} />
        )}
      </div>

      <div style={st.grid}>

        <div style={st.card}>
          <div style={st.cardTitle('#1a3a6e')}>📊 Hipotecas Activas</div>
          <div style={st.subblocks}>
            <button type="button" style={st.subblock('#eaf0fb')} onClick={() => onVerDetalleActivas('evaluacion')}>
              <div style={st.subblockLbl('#1a3a6e')}>En Evaluación</div>
              <div style={st.subblockQty('#1a3a6e')}>{activas.en_evaluacion.cantidad}</div>
              <div style={st.subblockAmt}>{money(activas.en_evaluacion.monto)}</div>
            </button>
            <button type="button" style={st.subblock('#e7f7ed')} onClick={() => onVerDetalleActivas('autorizadas')}>
              <div style={st.subblockLbl('#16a34a')}>Autorizadas</div>
              <div style={st.subblockQty('#16a34a')}>{activas.autorizadas.cantidad}</div>
              <div style={st.subblockAmt}>{money(activas.autorizadas.monto)}</div>
            </button>
            <button type="button" style={st.subblock('#fff1e6')} onClick={() => onVerDetalleActivas('avaluo')}>
              <div style={st.subblockLbl('#f97316')}>En Avalúo</div>
              <div style={st.subblockQty('#f97316')}>{activas.en_avaluo.cantidad}</div>
              <div style={st.subblockAmt}>{money(activas.en_avaluo.monto)}</div>
            </button>
            <button type="button" style={st.subblock('#dbeafe')} onClick={() => onVerDetalleActivas('infonavit')}>
              <div style={st.subblockLbl('#1e40af')}>En Infonavit</div>
              <div style={st.subblockQty('#1e40af')}>{activas.en_infonavit.cantidad}</div>
              <div style={st.subblockAmt}>{money(activas.en_infonavit.monto)}</div>
            </button>
          </div>
        </div>

        <div style={st.card}>
          <div style={st.cardTitle('#f97316')}>✍️ En Proceso de Firma</div>
          <div style={st.twoCol}>
            <div style={st.twoColItem(false)}>
              <div style={st.twoColLbl}>Cantidad</div>
              <div style={st.twoColQty('#f97316')}>{en_proceso_firma.cantidad}</div>
            </div>
            <div style={st.twoColItem(true)}>
              <div style={st.twoColLbl}>Monto Autorizado</div>
              <div style={st.twoColAmt('#f97316')}>{money(en_proceso_firma.monto)}</div>
            </div>
          </div>
        </div>

        <div style={st.cardCompact}>
          <div style={st.cardTitleCompact('#16a34a')}>✅ Hipotecas Firmadas — {firmadas.anio}</div>
          <div style={st.statCards}>
            <button type="button" style={st.statCard('#eef2ff')} onClick={() => onVerDetalleActivas('firmadas-mes')}>
              <div style={st.statCardIcon}>📅</div>
              <div style={st.statCardLbl}>Del Mes</div>
              <div style={st.statCardQty}>{delMes.cantidad}</div>
              <div style={st.statCardAmt('#4338ca')}>{money(delMes.monto)}</div>
            </button>
            <button type="button" style={st.statCard('#e7f7ed')} onClick={() => onVerDetalleActivas('firmadas-anio')}>
              <div style={st.statCardIcon}>📊</div>
              <div style={st.statCardLbl}>Del Año</div>
              <div style={st.statCardQty}>{firmadas.total.cantidad}</div>
              <div style={st.statCardAmt('#16a34a')}>{money(firmadas.total.monto)}</div>
            </button>
          </div>
        </div>

        <div style={st.cardCompact}>
          <div style={st.cardTitleCompact('#1a3a6e')}>💰 Por Cobrar</div>
          <div style={st.twoCol}>
            <div style={st.twoColItemCompact(false)}>
              <div style={st.twoColLblCompact}>Cantidad</div>
              <div style={st.twoColQtyCompact('#16a34a')}>{por_cobrar.cantidad}</div>
            </div>
            <div style={st.twoColItemCompact(true)}>
              <div style={st.twoColLblCompact}>Crédito $</div>
              <div style={st.twoColAmtCompact('#16a34a')}>{money(por_cobrar.monto)}</div>
            </div>
          </div>
        </div>

        <div style={{ ...st.card, gridColumn: '1 / -1' }}>
          <div style={st.cardTitle('#f97316')}>⚡ Acciones Rápidas</div>
          <div style={st.accionesRow}>
            <button style={st.btnPrimary} onClick={onVerListado}>🏦 Hipotecas</button>
            <button style={st.btnNotif} onClick={onVerNotificaciones}>
              🔔 Notificaciones {notifCount > 0 && <span style={st.badgeCount}>{notifCount}</span>}
            </button>
            <button style={st.btnPrimary} onClick={onVerReportes}>📊 Reportes</button>
            {onVerHistorico && <button style={st.btnPrimary} onClick={onVerHistorico}>📜 Histórico</button>}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SeguimientoDashboard;
